/**
 * Centralized web component registration
 *
 * This file ensures all Anear web components are registered before any HTML
 * is parsed, which is critical for Safari compatibility. Safari can be strict
 * about when custom elements are defined relative to when HTML containing them
 * is parsed.
 *
 * Import this file early in the app initialization to ensure components
 * are available before any HTML is rendered.
 */

// Import all web components (this triggers their registration)
import './AnearAnchor'
import './AnearQrCode'
import './AnearNav'
import './AnearAction'
import './AnearHelp'
import './AnearForm'
import './AnearTimeout'
import './AnearAutofocus'
import './AnearAutoScroll'
import './AnearUserAvatar'
import './AnearCounter'
import './AnearChoiceGroup'
import './AnearDropDown'
import './AnearApp'
import './AnearFileUpload'
import './AnearImageUpload'
import './AnearFramedImage'
import './AnearMediaUpload'
import './AnearVideo'
import './AnearWorkflow'
import './AnearCamera'

/**
 * Upgrade any existing custom elements in the document
 * This is useful if HTML was parsed before components were registered
 * Safari can be strict about this, so we need to handle it explicitly
 */
export function upgradeCustomElements(root = document) {
  // Get all potential custom element tags
  const customElementTags = [
    'anear-anchor',
    'anear-qr-code',
    'anear-nav',
    'anear-action',
    'anear-help',
    'anear-form',
    'anear-timeout',
    'anear-autofocus',
    'anear-autoscroll',
    'anear-user-avatar',
    'anear-counter',
    'anear-choice-group',
    'anear-drop-down',
    'anear-app',
    'anear-file-upload',
    'anear-image-upload',
    'anear-framed-image',
    'anear-media-upload',
    'anear-video',
    'anear-workflow',
    'anear-camera'
  ]

  customElementTags.forEach(tagName => {
    // Check if the custom element is defined
    if (!customElements.get(tagName)) {
      return // Component not registered yet, skip
    }

    // Find any elements that exist but aren't upgraded yet
    const elements = root.querySelectorAll(tagName)
    elements.forEach(el => {
      // Check if element needs upgrading:
      // 1. Constructor is HTMLElement (generic, not custom class)
      // 2. Element localName matches tagName (it's the right tag)
      // Note: Custom elements that are already upgraded will have their custom constructor
      const needsUpgrade = el.constructor === HTMLElement && el.localName === tagName
      
      if (needsUpgrade) {
        try {
          // Use customElements.upgrade if available (modern browsers)
          // This is the preferred method and works across Shadow DOM boundaries
          if (typeof customElements.upgrade === 'function') {
            customElements.upgrade(el)
          } else {
            // Fallback: force upgrade by cloning and replacing
            // Use the element's owner document (works for Shadow DOM too)
            const doc = el.ownerDocument || document
            const newEl = doc.createElement(tagName)
            Array.from(el.attributes).forEach(attr => {
              newEl.setAttribute(attr.name, attr.value)
            })
            newEl.innerHTML = el.innerHTML
            if (el.parentNode) {
              el.parentNode.replaceChild(newEl, el)
            }
          }
        } catch (e) {
          // Ignore upgrade errors - element might already be upgraded or upgrade failed
          console.warn(`Failed to upgrade ${tagName}:`, e)
        }
      }
    })
  })
}

// Auto-upgrade on DOMContentLoaded if document is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    upgradeCustomElements()
  })
} else {
  // Document is already loaded, upgrade immediately
  upgradeCustomElements()
}
